import { useState, useEffect } from "react";
import { 
  Store, Mail, Globe, Bell, Shield, CreditCard, BarChart3, 
  Check, X, Loader2, Save, LogOut, Key, ExternalLink, RefreshCw,
  Wallet, Smartphone, CheckCircle2, AlertCircle, FileCheck
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Elements, PaymentElement } from "@stripe/react-stripe-js";
import { getStripe } from "@/lib/stripe";

interface StoreSettings {
  id: string;
  store_name: string;
  support_email: string;
  logo_url: string | null;
  currency: string;
  timezone: string;
  notifications: {
    order_confirmation: boolean;
    failed_payment_alerts: boolean;
    daily_summary: boolean;
  };
}

interface IntegrationStatus {
  stripe: "connected" | "not_connected" | "checking";
  ga4: "connected" | "not_connected" | "checking";
}

// Helper component for payment method status badges
const PaymentMethodBadge = ({ status }: { status: 'checking' | 'available' | 'unavailable' | 'unknown' }) => {
  if (status === 'available') {
    return (
      <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-900/50">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        Available
      </Badge>
    );
  }
  if (status === 'unavailable') {
    return (
      <Badge className="bg-slate-700 text-slate-400 border-slate-600">
        <X className="w-3 h-3 mr-1" />
        Not on this device
      </Badge>
    );
  }
  if (status === 'checking') {
    return (
      <Badge className="bg-slate-700 text-slate-300">
        <Loader2 className="w-3 h-3 animate-spin mr-1" />
        Checking...
      </Badge>
    );
  }
  return (
    <Badge className="bg-slate-700 text-slate-300">
      Not checked
    </Badge>
  );
};

export function SettingsPage() {
  const { signOut, session } = useAuth();
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState<IntegrationStatus>({
    stripe: "checking",
    ga4: "checking",
  });
  const [isTestingGA4, setIsTestingGA4] = useState(false);
  
  // Payment health check state
  const [paymentHealthCheck, setPaymentHealthCheck] = useState<{
    isChecking: boolean;
    applePay: 'checking' | 'available' | 'unavailable' | 'unknown';
    googlePay: 'checking' | 'available' | 'unavailable' | 'unknown';
    link: 'checking' | 'available' | 'unavailable' | 'unknown';
    card: 'checking' | 'available' | 'unavailable' | 'unknown';
    domainVerification: 'checking' | 'verified' | 'missing' | 'error';
    stripeTestMode: boolean | null;
  }>({
    isChecking: false,
    applePay: 'unknown',
    googlePay: 'unknown',
    link: 'unknown',
    card: 'unknown',
    domainVerification: 'checking',
    stripeTestMode: null,
  });

  // Fetch settings and check domain verification on mount
  useEffect(() => {
    fetchSettings();
    checkIntegrations();
    checkApplePayDomainVerification();
  }, []);

  // Check Apple Pay domain verification file
  const checkApplePayDomainVerification = async () => {
    try {
      const response = await fetch('/.well-known/apple-developer-merchantid-domain-association');
      if (response.ok) {
        const text = await response.text();
        if (text && text.length > 100) {
          setPaymentHealthCheck(prev => ({ ...prev, domainVerification: 'verified' }));
        } else {
          setPaymentHealthCheck(prev => ({ ...prev, domainVerification: 'missing' }));
        }
      } else {
        setPaymentHealthCheck(prev => ({ ...prev, domainVerification: 'missing' }));
      }
    } catch {
      setPaymentHealthCheck(prev => ({ ...prev, domainVerification: 'error' }));
    }
  };

  // Run payment health check
  const runPaymentHealthCheck = async () => {
    setPaymentHealthCheck(prev => ({ ...prev, isChecking: true }));
    
    // Check Stripe mode from publishable key
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    const isTestMode = publishableKey?.startsWith('pk_test_');
    
    setPaymentHealthCheck(prev => ({
      ...prev,
      stripeTestMode: isTestMode,
      card: 'available', // Card is always available
      link: 'available', // Link is enabled via automatic_payment_methods
    }));

    // Check Apple Pay availability (Safari on Apple devices)
    const isAppleDevice = /iPad|iPhone|iPod|Mac/.test(navigator.userAgent) && 
                          /Safari/.test(navigator.userAgent);
    
    // Check Google Pay availability (Chrome/Android)
    const isGooglePayCapable = /Android/.test(navigator.userAgent) || 
                               /Chrome/.test(navigator.userAgent);

    setPaymentHealthCheck(prev => ({
      ...prev,
      isChecking: false,
      applePay: isAppleDevice ? 'available' : 'unavailable',
      googlePay: isGooglePayCapable ? 'available' : 'unavailable',
    }));

    // Re-check domain verification
    await checkApplePayDomainVerification();

    toast.success("Payment health check complete");
  };

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("store_settings")
        .select("*")
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        // Parse notifications if it's a string
        const notifications = typeof data.notifications === 'string' 
          ? JSON.parse(data.notifications)
          : data.notifications;
        
        setSettings({
          ...data,
          notifications: notifications || {
            order_confirmation: true,
            failed_payment_alerts: true,
            daily_summary: false,
          }
        } as StoreSettings);
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
      toast.error("Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  };

  const checkIntegrations = async () => {
    // Check Stripe (we assume connected if secret key exists in edge function)
    setIntegrationStatus(prev => ({ ...prev, stripe: "connected" }));

    // Check GA4 by testing the connection
    try {
      const response = await supabase.functions.invoke("ga-analytics", {
        body: { range: "7d" },
      });
      
      if (response.error || response.data?.error) {
        setIntegrationStatus(prev => ({ ...prev, ga4: "not_connected" }));
      } else {
        setIntegrationStatus(prev => ({ ...prev, ga4: "connected" }));
      }
    } catch {
      setIntegrationStatus(prev => ({ ...prev, ga4: "not_connected" }));
    }
  };

  const handleTestGA4 = async () => {
    setIsTestingGA4(true);
    try {
      const response = await supabase.functions.invoke("ga-analytics", {
        body: { range: "7d" },
      });

      if (response.error) {
        toast.error("GA4 connection failed: " + response.error.message);
        setIntegrationStatus(prev => ({ ...prev, ga4: "not_connected" }));
      } else if (response.data?.error) {
        toast.error("GA4 connection failed: " + response.data.error);
        setIntegrationStatus(prev => ({ ...prev, ga4: "not_connected" }));
      } else {
        toast.success("GA4 connection successful!");
        setIntegrationStatus(prev => ({ ...prev, ga4: "connected" }));
      }
    } catch (err) {
      console.error("GA4 test error:", err);
      toast.error("Failed to test GA4 connection");
      setIntegrationStatus(prev => ({ ...prev, ga4: "not_connected" }));
    } finally {
      setIsTestingGA4(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("store_settings")
        .update({
          store_name: settings.store_name,
          support_email: settings.support_email,
          logo_url: settings.logo_url,
          currency: settings.currency,
          timezone: settings.timezone,
          notifications: settings.notifications,
        })
        .eq("id", settings.id);

      if (error) throw error;
      toast.success("Settings saved");
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleNotificationToggle = (key: keyof StoreSettings["notifications"]) => {
    if (!settings) return;
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [key]: !settings.notifications[key],
      },
    });
  };

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success("Logged out successfully");
    } catch {
      toast.error("Failed to log out");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Store Profile */}
      <Card className="bg-slate-800/50 border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-emerald-900/30 rounded-lg flex items-center justify-center">
            <Store className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-100">Store Profile</h3>
            <p className="text-slate-400 text-sm">Basic store information</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300">Store Name</Label>
            <Input
              value={settings?.store_name || ""}
              onChange={(e) => setSettings(prev => prev ? { ...prev, store_name: e.target.value } : null)}
              className="bg-slate-900 border-slate-700 text-slate-100 mt-1"
            />
          </div>
          <div>
            <Label className="text-slate-300">Support Email</Label>
            <Input
              type="email"
              value={settings?.support_email || ""}
              onChange={(e) => setSettings(prev => prev ? { ...prev, support_email: e.target.value } : null)}
              className="bg-slate-900 border-slate-700 text-slate-100 mt-1"
            />
          </div>
          <div>
            <Label className="text-slate-300">Currency</Label>
            <Select
              value={settings?.currency || "GBP"}
              onValueChange={(value) => setSettings(prev => prev ? { ...prev, currency: value } : null)}
            >
              <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-100 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GBP">GBP (£)</SelectItem>
                <SelectItem value="USD">USD ($)</SelectItem>
                <SelectItem value="EUR">EUR (€)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Timezone</Label>
            <Select
              value={settings?.timezone || "Europe/London"}
              onValueChange={(value) => setSettings(prev => prev ? { ...prev, timezone: value } : null)}
            >
              <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-100 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Europe/London">London (GMT/BST)</SelectItem>
                <SelectItem value="America/New_York">New York (EST/EDT)</SelectItem>
                <SelectItem value="America/Los_Angeles">Los Angeles (PST/PDT)</SelectItem>
                <SelectItem value="Europe/Paris">Paris (CET/CEST)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-slate-300">Logo URL</Label>
            <Input
              value={settings?.logo_url || ""}
              onChange={(e) => setSettings(prev => prev ? { ...prev, logo_url: e.target.value } : null)}
              placeholder="https://..."
              className="bg-slate-900 border-slate-700 text-slate-100 mt-1"
            />
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </Card>

      {/* Notifications */}
      <Card className="bg-slate-800/50 border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-900/30 rounded-lg flex items-center justify-center">
            <Bell className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-100">Notifications</h3>
            <p className="text-slate-400 text-sm">Email notification preferences</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-slate-700/50">
            <div>
              <div className="text-slate-100 font-medium">Order Confirmation Emails</div>
              <div className="text-slate-400 text-sm">Send confirmation emails to customers after purchase</div>
            </div>
            <Switch
              checked={settings?.notifications.order_confirmation ?? true}
              onCheckedChange={() => handleNotificationToggle("order_confirmation")}
            />
          </div>

          <div className="flex items-center justify-between py-3 border-b border-slate-700/50">
            <div>
              <div className="text-slate-100 font-medium">Failed Payment Alerts</div>
              <div className="text-slate-400 text-sm">Get notified when a payment fails</div>
            </div>
            <Switch
              checked={settings?.notifications.failed_payment_alerts ?? true}
              onCheckedChange={() => handleNotificationToggle("failed_payment_alerts")}
            />
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-slate-100 font-medium">Daily Summary Emails</div>
              <div className="text-slate-400 text-sm">Receive a daily summary of orders and revenue</div>
            </div>
            <Switch
              checked={settings?.notifications.daily_summary ?? false}
              onCheckedChange={() => handleNotificationToggle("daily_summary")}
            />
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Preferences
          </Button>
        </div>
      </Card>

      {/* Integrations */}
      <Card className="bg-slate-800/50 border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-900/30 rounded-lg flex items-center justify-center">
            <Globe className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-100">Integrations</h3>
            <p className="text-slate-400 text-sm">Connected services and APIs</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Stripe */}
          <div className="flex items-center justify-between py-4 px-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-900/30 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <div className="text-slate-100 font-medium">Stripe</div>
                <div className="text-slate-400 text-sm">Payment processing</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {integrationStatus.stripe === "checking" ? (
                <Badge className="bg-slate-700 text-slate-300">
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  Checking...
                </Badge>
              ) : integrationStatus.stripe === "connected" ? (
                <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-900/50">
                  <Check className="w-3 h-3 mr-1" />
                  Connected
                </Badge>
              ) : (
                <Badge className="bg-red-900/30 text-red-400 border-red-900/50">
                  <X className="w-3 h-3 mr-1" />
                  Not Connected
                </Badge>
              )}
            </div>
          </div>

          {/* GA4 */}
          <div className="flex items-center justify-between py-4 px-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-900/30 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <div className="text-slate-100 font-medium">Google Analytics 4</div>
                <div className="text-slate-400 text-sm">
                  Property ID: 517923411 • Measurement ID: G-BQN30YP256
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {integrationStatus.ga4 === "checking" ? (
                <Badge className="bg-slate-700 text-slate-300">
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  Checking...
                </Badge>
              ) : integrationStatus.ga4 === "connected" ? (
                <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-900/50">
                  <Check className="w-3 h-3 mr-1" />
                  Connected
                </Badge>
              ) : (
                <Badge className="bg-red-900/30 text-red-400 border-red-900/50">
                  <X className="w-3 h-3 mr-1" />
                  Not Connected
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleTestGA4}
                disabled={isTestingGA4}
                className="border-slate-600 text-slate-300 h-8"
              >
                {isTestingGA4 ? (
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="w-3 h-3 mr-1" />
                )}
                Test Connection
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Payments Health Check */}
      <Card className="bg-slate-800/50 border-slate-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100">Payments Health Check</h3>
              <p className="text-slate-400 text-sm">Verify payment methods and wallet support</p>
            </div>
          </div>
          <Button
            onClick={runPaymentHealthCheck}
            disabled={paymentHealthCheck.isChecking}
            className="bg-green-600 hover:bg-green-700"
          >
            {paymentHealthCheck.isChecking ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Run Health Check
          </Button>
        </div>

        <div className="space-y-3">
          {/* Stripe Mode */}
          <div className="flex items-center justify-between py-3 px-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-slate-400" />
              <span className="text-slate-100">Stripe Mode</span>
            </div>
            <Badge className={paymentHealthCheck.stripeTestMode === null 
              ? "bg-slate-700 text-slate-300"
              : paymentHealthCheck.stripeTestMode 
                ? "bg-amber-900/30 text-amber-400 border-amber-900/50"
                : "bg-emerald-900/30 text-emerald-400 border-emerald-900/50"
            }>
              {paymentHealthCheck.stripeTestMode === null 
                ? "Not checked" 
                : paymentHealthCheck.stripeTestMode 
                  ? "⚠️ Test Mode" 
                  : "✓ Live Mode"}
            </Badge>
          </div>

          {/* Card Payments */}
          <div className="flex items-center justify-between py-3 px-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-slate-400" />
              <span className="text-slate-100">Card Payments</span>
            </div>
            <PaymentMethodBadge status={paymentHealthCheck.card} />
          </div>

          {/* Apple Pay */}
          <div className="flex items-center justify-between py-3 px-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-slate-400" />
              <div>
                <span className="text-slate-100">Apple Pay</span>
                <p className="text-slate-500 text-xs">Shows on Safari / Apple devices</p>
              </div>
            </div>
            <PaymentMethodBadge status={paymentHealthCheck.applePay} />
          </div>

          {/* Google Pay */}
          <div className="flex items-center justify-between py-3 px-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-slate-400" />
              <div>
                <span className="text-slate-100">Google Pay</span>
                <p className="text-slate-500 text-xs">Shows on Chrome / Android devices</p>
              </div>
            </div>
            <PaymentMethodBadge status={paymentHealthCheck.googlePay} />
          </div>

          {/* Stripe Link */}
          <div className="flex items-center justify-between py-3 px-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center gap-3">
              <ExternalLink className="w-5 h-5 text-slate-400" />
              <div>
                <span className="text-slate-100">Stripe Link</span>
                <p className="text-slate-500 text-xs">One-click checkout for returning customers</p>
              </div>
            </div>
            <PaymentMethodBadge status={paymentHealthCheck.link} />
          </div>

          {/* Apple Pay Domain Verification */}
          <div className="flex items-center justify-between py-3 px-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center gap-3">
              <FileCheck className="w-5 h-5 text-slate-400" />
              <div>
                <span className="text-slate-100">Apple Pay Domain Verification</span>
                <p className="text-slate-500 text-xs">Required file at /.well-known/</p>
              </div>
            </div>
            <Badge className={
              paymentHealthCheck.domainVerification === 'verified'
                ? "bg-emerald-900/30 text-emerald-400 border-emerald-900/50"
                : paymentHealthCheck.domainVerification === 'missing'
                  ? "bg-red-900/30 text-red-400 border-red-900/50"
                  : "bg-slate-700 text-slate-300"
            }>
              {paymentHealthCheck.domainVerification === 'verified' && <CheckCircle2 className="w-3 h-3 mr-1" />}
              {paymentHealthCheck.domainVerification === 'missing' && <AlertCircle className="w-3 h-3 mr-1" />}
              {paymentHealthCheck.domainVerification === 'checking' && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              {paymentHealthCheck.domainVerification === 'verified' 
                ? "Verified" 
                : paymentHealthCheck.domainVerification === 'missing'
                  ? "Missing"
                  : paymentHealthCheck.domainVerification === 'error'
                    ? "Error"
                    : "Checking..."}
            </Badge>
          </div>
        </div>

        {/* Stripe Dashboard Action */}
        <div className="mt-6 p-4 bg-indigo-900/20 border border-indigo-900/30 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-indigo-400 mt-0.5" />
            <div>
              <p className="text-slate-200 font-medium">Complete Apple Pay Setup in Stripe Dashboard</p>
              <p className="text-slate-400 text-sm mt-1">
                After deploying, you must register your domain in Stripe Dashboard → Settings → Payment methods → Apple Pay to complete the setup.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 border-indigo-900/50 text-indigo-400 hover:bg-indigo-900/20"
                onClick={() => window.open('https://dashboard.stripe.com/settings/payment_methods', '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Stripe Dashboard
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Security / Admin */}
      <Card className="bg-slate-800/50 border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-red-900/30 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-100">Security & Admin</h3>
            <p className="text-slate-400 text-sm">Account security and access management</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-4 px-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center gap-3">
              <Key className="w-5 h-5 text-slate-400" />
              <div>
                <div className="text-slate-100 font-medium">Password</div>
                <div className="text-slate-400 text-sm">Change your account password</div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300"
              onClick={() => toast.info("Password change coming soon")}
            >
              Change Password
            </Button>
          </div>

          <div className="flex items-center justify-between py-4 px-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center gap-3">
              <LogOut className="w-5 h-5 text-slate-400" />
              <div>
                <div className="text-slate-100 font-medium">Sign Out</div>
                <div className="text-slate-400 text-sm">Log out of your account</div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-red-900/50 text-red-400 hover:bg-red-900/20"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>

        {session && (
          <div className="mt-6 pt-4 border-t border-slate-700/50">
            <p className="text-slate-500 text-xs">
              Logged in as: <span className="text-slate-400">{session.user?.email}</span>
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}